import React, { useEffect, useState } from 'react';

interface VideoSuggestion {
  id: string;
  title: string;
  channelTitle: string;
  thumbnailUrl: string;
}

interface VideoSuggestionsProps {
  videoId: string;
  onVideoSelect: (videoId: string, title: string) => void;
}

const VideoSuggestions: React.FC<VideoSuggestionsProps> = ({ videoId, onVideoSelect }) => {
  const [suggestions, setSuggestions] = useState<VideoSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!videoId) return;

      try {
        setLoading(true);
        setError(null);

        const apiKey = import.meta.env.VITE_YOUTUBE_API_KEY;
        if (!apiKey) {
          throw new Error('YouTube API key is not configured');
        }

        // Validate video ID format
        if (!/^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
          throw new Error('Invalid video ID format');
        }

        // Step 1: Fetch the original video's metadata to get its title
        const videoUrl = new URL('https://www.googleapis.com/youtube/v3/videos');
        videoUrl.searchParams.append('part', 'snippet');
        videoUrl.searchParams.append('id', videoId);
        videoUrl.searchParams.append('key', apiKey);

        console.log('Video ID:', videoId);
        console.log('Video Request URL:', videoUrl.toString());

        const videoResponse = await fetch(videoUrl);
        const videoData = await videoResponse.json();

        if (!videoResponse.ok) {
          console.error('Full YouTube API Error:', videoData);
          const errorDetails = videoData.error?.errors?.[0]?.reason || videoData.error?.message;
          if (errorDetails === 'quotaExceeded') {
            throw new Error('Recommendation quota exceeded for today. Please try again tomorrow. The rest of the app is still functional.');
          }
          throw new Error(`API Error: ${errorDetails}`);
        }

        if (!videoData.items || videoData.items.length === 0) {
          throw new Error('Video not found');
        }

        const videoTitle = videoData.items[0].snippet.title;
        console.log('Video Title:', videoTitle);

        // Step 2: Search for related videos using the video title
        const searchUrl = new URL('https://www.googleapis.com/youtube/v3/search');
        const searchParams = {
          part: 'snippet',
          q: videoTitle,
          type: 'video',
          maxResults: '4', // Fetch 4 to filter out the original video
          key: apiKey,
          order: 'relevance',
        };

        Object.entries(searchParams).forEach(([key, value]) => {
          searchUrl.searchParams.append(key, value);
        });

        console.log('Search Request URL:', searchUrl.toString());

        const searchResponse = await fetch(searchUrl);
        const searchData = await searchResponse.json();

        if (!searchResponse.ok) {
          console.error('Full YouTube API Error:', searchData);
          const errorDetails = searchData.error?.errors?.[0]?.reason || searchData.error?.message;
          if (errorDetails === 'quotaExceeded') {
            throw new Error('Recommendation quota exceeded for today. Please try again tomorrow. The rest of the app is still functional.');
          }
          throw new Error(`API Error: ${errorDetails}`);
        }

        console.log('API Response Data:', searchData);

        if (!searchData.items || !Array.isArray(searchData.items)) {
          throw new Error('Invalid response format from YouTube API');
        }

        // Filter out the original video and format suggestions
        const formattedSuggestions = searchData.items
          .filter(item => item.id?.videoId && item.snippet && item.id.videoId !== videoId)
          .slice(0, 3) // Limit to 3 suggestions
          .map(item => ({
            id: item.id.videoId,
            title: item.snippet.title,
            channelTitle: item.snippet.channelTitle,
            thumbnailUrl: item.snippet.thumbnails?.medium?.url || item.snippet.thumbnails?.default?.url,
          }));

        setSuggestions(formattedSuggestions);
      } catch (err) {
        console.error('Video suggestions error:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [videoId]);

  if (!videoId) {
    return (
      <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <p className="text-center text-gray-500 dark:text-gray-400">
          Your suggestions will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <p className="text-center text-red-600 dark:text-red-400">
            {error}
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse flex flex-col bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
              <div className="w-full aspect-video bg-gray-200 dark:bg-gray-700 rounded mb-3"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results */}
      {!loading && !error && suggestions.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              onClick={() => onVideoSelect(suggestion.id, suggestion.title)}
              className="flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left overflow-hidden"
            >
              <div className="relative w-full aspect-video">
                <img
                  src={suggestion.thumbnailUrl}
                  alt={suggestion.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="p-3">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
                  {suggestion.title}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {suggestion.channelTitle}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && suggestions.length === 0 && (
        <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <p className="text-center text-gray-500 dark:text-gray-400">
            No suggestions available
          </p>
        </div>
      )}
    </div>
  );
};

export default VideoSuggestions;